import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useBlogs, useSearchBlogs } from "../../hooks/useBlog";
import { AccentText, Body, Headline } from "../brand";
import Button from "../Button";
import { IconHeart, IconLeaf, IconSearch } from "../brand/icons";
import { ALL_TOPICS, tagsOf } from "./blogTopics";

const PILL_LIMIT = 5;

/**
 * Blog hero — design/stitch/blog.html.
 * Search keeps the existing `get_blog/?search_text=` flow; the topic pills are
 * derived from the tags the CMS actually returns, so every pill has matches.
 */
const Blog1 = ({ category = ALL_TOPICS, onCategoryChange }) => {
  const [text, setText] = useState("");
  const { data: allBlogs, refetch: refetchAll } = useBlogs();
  const { refetch: refetchSearch } = useSearchBlogs(text);

  const categories = useMemo(() => {
    const counts = new Map();
    (allBlogs?.data ?? []).forEach((item) => {
      tagsOf(item).forEach((label) => {
        const key = label.toLowerCase();
        const hit = counts.get(key);
        if (hit) hit.count += 1;
        else counts.set(key, { label, count: 1 });
      });
    });
    return [...counts.values()]
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .slice(0, PILL_LIMIT)
      .map((entry) => entry.label);
  }, [allBlogs]);

  const handleSearch = async (e) => {
    e.preventDefault();

    try {
      const result = await refetchSearch();
      if (result?.data?.data?.length === 0 || !result?.data?.data?.length) {
        refetchAll();
        toast.error("No blogs found");
      }
      setText("");
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  const pillClass = (isActive) =>
    `rounded-full px-4 py-2 text-label-sm transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue ${
      isActive
        ? "bg-spectrum-blue text-surface-white shadow-brand-sm"
        : "bg-cloud-mist text-spectrum-blue hover:bg-mist-deep"
    }`;

  return (
    <section className="relative w-full overflow-hidden bg-hero-wash px-5 pt-12 pb-14 sm:px-6 md:px-10 md:pt-14 md:pb-16">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 left-1/2 h-[350px] w-[900px] max-w-full -translate-x-1/2 rounded-full bg-mist-glow blur-3xl"
      />

      <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center">
        <div className="mb-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-heart-teal">
          <IconHeart filled size={20} className="shrink-0" />
          <AccentText className="!whitespace-normal !text-center">
            Stories, insights &amp; lived experiences
          </AccentText>
          <IconLeaf size={20} className="shrink-0" />
        </div>

        <Headline light="Our" bold="Blog" as="h1" className="mb-4 !text-center" />

        <Body className="mb-8 max-w-2xl !text-center !text-body-lg text-muted">
          Empowering perspectives, practical guides, and neurodiversity-affirming
          stories written by parents, allies, and specialists.
        </Body>

        <form
          className="mb-6 w-full max-w-2xl"
          onSubmit={(e) => handleSearch(e)}
          role="search"
        >
          <label htmlFor="blog-search" className="sr-only">
            Search blogs
          </label>
          <div className="flex items-center gap-1.5 rounded-2xl border-[1.5px] border-cloud-mist bg-surface-white p-1.5 shadow-brand-sm transition-all duration-200 focus-within:border-sky-blue focus-within:shadow-brand-md">
            <IconSearch size={22} className="ml-2 shrink-0 text-muted" />
            <input
              id="blog-search"
              type="text"
              name="search"
              onChange={(e) => setText(e.target.value)}
              value={text}
              placeholder="Search articles, topics, or guides..."
              className="w-full min-w-0 border-0 bg-transparent px-2 py-2 text-body-md text-ink outline-none placeholder-village sm:px-3"
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!text.trim()}
              className="shrink-0 !px-4 sm:!px-6"
            >
              Search
            </Button>
          </div>
        </form>

        <div
          className="flex flex-wrap items-center justify-center gap-2 pt-1 sm:gap-2.5"
          role="group"
          aria-label="Filter articles by topic"
        >
          {[ALL_TOPICS, ...categories].map((label) => {
            const isActive = label === category;
            return (
              <button
                key={label}
                type="button"
                aria-pressed={isActive}
                className={pillClass(isActive)}
                onClick={() => onCategoryChange?.(label)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Blog1;
